import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { FORM_CODE, FORM_ID, IELTData } from 'legacy/lut/classes/models/partner360/ELT/IELTData'
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react'

import { CrudStore } from 'core/types/crudStore'
import Logger from 'core/utils/Logger'

import { getFinancialDataHelper } from '../../../helpers/requestsHelpers/getFinancialDataHelper'

interface IProps {
  data?: IELTData
  partnerId: string
  isRollup: number
  setData: Dispatch<SetStateAction<IELTData | undefined>>
}

export const useRoundTable = ({ data, partnerId, isRollup, setData }: IProps) => {
  const request = useMemo(
    () => ({
      entryId: data?.EntryId,
      uid: data?.UID,
      partnerId: data?.PartnerId || partnerId,
      groupId: data?.GroupId || null,
      fYear: data?.FYear,
      fQuarter: data?.FQuarter,
      // Legacy documents without EltType are rendered as PPTX by the form.
      eltType: data?.EltType || EltType.Pptx,
      isRollup,
    }),
    [
      data?.EntryId,
      data?.UID,
      data?.PartnerId,
      data?.GroupId,
      data?.FYear,
      data?.FQuarter,
      data?.EltType,
      partnerId,
      isRollup,
    ]
  )
  const requestKey = JSON.stringify(request)
  const [result, setResult] = useState<{ requestKey: string; eligible: boolean }>()
  // Hide the previous document’s result while the current request is loading.
  const rtEligible = result?.requestKey === requestKey ? result.eligible : false

  useEffect(() => {
    let active = true
    const { fYear, eltType } = request
    if (!fYear || !eltType) return

    getFinancialDataHelper({ ...request, fYear, eltType })
      .then((financialData) => {
        if (active) setResult({ requestKey, eligible: financialData?.RTEligible === 1 })
      })
      .catch((error) => {
        Logger.error('Unable to load Round Table eligibility', error)
        if (active) setResult({ requestKey, eligible: false })
      })

    return () => {
      active = false
    }
  }, [request, requestKey])

  const onToggleRoundTable = useCallback(
    async (ready: boolean) => {
      const { entryId, uid } = request
      if (!rtEligible || !entryId || entryId === -1) {
        throw new Error('Round Table setting cannot be saved for this document')
      }

      const RTReady = Number(ready)
      const saved = await CrudStore.get<IELTData>(CrudStoreItemPartner360.JBPEltData).update(
        { formId: String(FORM_ID), code: FORM_CODE },
        [{ EntryId: entryId, UID: uid, RTReady }]
      )
      if (!saved.length) throw new Error('Round Table setting was not saved')

      setData((current) =>
        current?.EntryId === entryId && current?.UID === uid ? { ...current, RTReady } : current
      )
    },
    [request, rtEligible, setData]
  )

  return { rtEligible, onToggleRoundTable }
}
